import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';

import { definitionsApi } from '../api/definitions-api';
import { internalCatalogueApi } from '../api/internal-catalogue-api';
import type { IInternalFieldDetail } from '../api/internal-catalogue-contracts';
import {
  AdminPage,
  LoadError,
  STAFF_FIELD_TABS,
  useAdminData
} from '../components/admin-page';
import { NewStaffField } from './staff-fields/new-staff-field';
import { StaffFieldDetail } from './staff-fields/staff-field-detail';

/**
 * The staff-field catalogue (SR-226) — what a field **is**, as against what a record holds in
 * it. survey-api's §8, admin alone (`internal_fields.author`, its reads included).
 *
 * **Its own screen, not a fourth tab of the survey builder.** The builder authors what a
 * respondent is asked; these are the fields a respondent must never see, and REC-03 exists to
 * keep the two apart — "every field carries an explicit advisor-visible or internal-only flag",
 * with inference named as an unacceptable control. The builder's model is versioned and frozen
 * on publish; the catalogue is neither, so a tab beside "Versions" would teach the wrong rule.
 *
 * **Filtered here, not by the server.** `GET /admin/internal-fields` takes `surveyCode` and
 * `includeRetired` and no text filter, and answers the whole catalogue unpaged — 738 fields
 * after the Salesforce import. So the survey and the retired toggle are the server's, and the
 * search box filters what was read: the team firm register's rule, and for its reason — here it
 * filters the result rather than one page of it.
 *
 * **The survey list is the definitions surface's** (`definitionsApi.surveys`), not derived from
 * the bindings of whatever happens to be loaded: a survey nobody has bound a field to yet must
 * still be choosable, or the first field of a survey could never be placed.
 *
 * **Origin is drawn, and nothing is disabled by it.** `migrated` came from the Salesforce
 * import, `authored` from a person here. survey-api refuses neither, and its import is written
 * to leave a person's change alone, so the screen says where a field came from and what a later
 * import may do — it does not grey out a control the server would accept. The one thing truly
 * closed is `type` and `scope` after creation (`400 immutable-member`), and that is said where
 * they are drawn.
 */
export function AdminStaffFieldsPage() {
  const [search, setSearch] = React.useState('');
  const [surveyCode, setSurveyCode] = React.useState(EVERY_SURVEY);
  const [includeRetired, setIncludeRetired] = React.useState(false);
  const [selectedCode, setSelectedCode] = React.useState<Nullable<string>>(null);
  const [creating, setCreating] = React.useState(false);

  const { data: fields, error, setError, reload } = useAdminData<IInternalFieldDetail[]>(
    (signal) =>
      internalCatalogueApi.list(
        {
          surveyCode: surveyCode === EVERY_SURVEY ? undefined : surveyCode,
          includeRetired
        },
        signal
      ),
    [surveyCode, includeRetired]
  );

  const { data: surveys } = useAdminData(
    (signal) => definitionsApi.surveys(signal),
    []
  );

  // Not debounced: `useDebounced` is for a filter the **server** applies, and this one re-filters
  // a list already in memory — its own doc says so.
  const shown = React.useMemo(() => matching(fields ?? [], search), [fields, search]);
  // The card is the field the author opened, or the first one — and **nothing** once a filter
  // has dropped the one that was open. Falling through to `shown[0]` there would leave Delete
  // and Retire pointing at a field nobody chose.
  const selected =
    selectedCode === null
      ? (shown[0] ?? null)
      : (shown.find((field) => field.code === selectedCode) ?? null);

  return (
    <AdminPage title="Staff fields" tabs={STAFF_FIELD_TABS}>
      <Alert>
        <AlertTitle>Veritant’s own fields, never an advisor’s</AlertTitle>
        <AlertDescription>
          These are the fields staff fill in about a record — the eligibility switch, the quality
          rating, a due-diligence note. A field authored here appears on the record page of every
          survey it is bound to and on that survey’s queues, and on no advisor-facing screen or
          export, ever. A new field starts empty for everybody: nothing is filled in behind it.
        </AlertDescription>
      </Alert>

      {error && <LoadError message={error} />}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Search code or name…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-xs"
          aria-label="Search the catalogue"
        />
        <Select value={surveyCode} onValueChange={setSurveyCode}>
          <SelectTrigger className="w-56" aria-label="Survey">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EVERY_SURVEY}>Every survey</SelectItem>
            {(surveys ?? []).map((survey) => (
              <SelectItem key={survey.code} value={survey.code}>
                {survey.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={includeRetired}
            onCheckedChange={(checked) => setIncludeRetired(checked === true)}
          />
          Show retired
        </label>
        <span className="text-sm text-muted-foreground">
          {shown.length} of {fields?.length ?? 0}
        </span>
        <Button className="ml-auto" onClick={() => setCreating(true)}>
          <Plus />
          New field
        </Button>
      </div>

      {creating && (
        <NewStaffField
          surveys={surveys ?? []}
          onCreated={(code) => {
            setCreating(false);
            setSelectedCode(code);
            void reload();
          }}
          onCancel={() => setCreating(false)}
          onFailed={setError}
        />
      )}

      {fields === null ? (
        <Skeleton className="h-64 w-full" />
      ) : shown.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Nothing here yet</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {(fields.length === 0
              ? 'No field is bound to this survey.'
              : 'No field here matches that.') +
              (includeRetired ? '' : ' Retired fields are hidden — the box above shows them.')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
          <nav
            aria-label="Staff fields"
            className="grid max-h-[40rem] content-start gap-1 overflow-y-auto"
          >
            {shown.map((field) => (
              <button
                key={field.code}
                type="button"
                onClick={() => setSelectedCode(field.code)}
                className={
                  'rounded-md px-3 py-2 text-left text-sm transition-colors ' +
                  (selected?.code === field.code
                    ? 'bg-[var(--color-slight-blue)] font-medium text-[var(--color-royal-blue)]'
                    : 'hover:bg-[var(--color-slight-blue)]/60')
                }
              >
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <span>{field.name}</span>
                  {field.retired && <Badge variant="outline">retired</Badge>}
                </span>
                <span className="block font-mono text-xs text-muted-foreground">{field.code}</span>
                <span className="block text-xs text-muted-foreground">
                  {field.type} · {describeBindings(field)}
                </span>
              </button>
            ))}
          </nav>

          {selected === null ? (
            <p className="text-sm text-muted-foreground">
              The field you had open is not in this view. Clear the search, or choose another.
            </p>
          ) : (
            <StaffFieldDetail
              key={selected.code}
              field={selected}
              surveys={surveys ?? []}
              onChanged={() => void reload()}
              onRemoved={() => {
                setSelectedCode(null);
                void reload();
              }}
              onFailed={setError}
            />
          )}
        </div>
      )}
    </AdminPage>
  );
}

/** The survey filter's "no filter" value: a `Select` cannot hold an empty string as an item. */
const EVERY_SURVEY = 'every';

/** What the catalogue has no filter for. Code and name both, because an author looking for
 *  "quality" knows the words and an author repairing an import knows the code. */
function matching(fields: IInternalFieldDetail[], search: string): IInternalFieldDetail[] {
  const needle = search.trim().toLowerCase();
  if (needle === '') return fields;
  return fields.filter(
    (field) =>
      field.code.toLowerCase().includes(needle) || field.name.toLowerCase().includes(needle)
  );
}

/** Where the field is drawn, in the list's one line: the survey codes it is bound to, or that
 *  it is bound to none — which is a real state and the reason a field can exist unseen. */
function describeBindings(field: IInternalFieldDetail): string {
  if (field.bindings.length === 0) return 'on no survey';
  return field.bindings.map((binding) => binding.surveyCode).join(', ');
}
